import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import PageHeader from "../components/PageHeader";
import OraTimeButton from "../components/voice/OraTimeButton";
import OrientationControlButton from "../components/OrientationControlButton";

/**
 * Utility — elenco degli strumenti del TIME Watch.
 *
 * Ordine ufficiale:
 *  1. Timer
 *  2. Promemoria
 *  3. Cronometro   ← ora con icona 3D premium (stopwatch nero/oro renderizzato)
 *  4. Sveglia
 *  5. Impronta
 *  6. Istanti
 *
 * Tutte le utility usano un'icona PNG ad alta qualità per mantenere una
 * coerenza grafica assoluta con l'ecosistema TIME (stile realistico/renderizzato).
 * Nessuna icona è vettoriale: ogni nuova utility dovrà seguire lo stesso
 * linguaggio visivo per non spezzare l'uniformità premium.
 */
const UTILITIES = [
  { slug: "timer", icon: "/icons/timewatch/timer.png", title: "Timer", to: "/innovazione/time-watch/utility/timer", ready: true },
  { slug: "promemoria", icon: "/icons/timewatch/promemoria.png", title: "Promemoria", to: "/innovazione/time-watch/utility/promemoria", ready: true },
  { slug: "cronometro", icon: "/icons/timewatch/cronometro.png", title: "Cronometro", to: "/innovazione/time-watch/utility/cronometro", ready: true },
  { slug: "sveglia", icon: "/icons/timewatch/sveglia.png", title: "Sveglia", to: "/innovazione/time-watch/utility/sveglia", ready: true },
  { slug: "impronta", icon: "/icons/timewatch/impronta.png", title: "Impronta", to: "/innovazione/time-watch/utility/impronta", ready: true },
  { slug: "istanti", icon: "/icons/timewatch/istanti.png", title: "Istante", to: "/innovazione/time-watch/utility/istanti", ready: true },
];

export default function TimeWatchUtilityPage() {
  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-6xl mx-auto">
      <Link
        to="/innovazione/time-watch"
        data-testid="time-watch-utility-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        TIME Watch
      </Link>

      <PageHeader
        eyebrow="TIME Watch"
        title="Utility"
        testId="time-watch-utility-header"
      />

      <div
        data-testid="time-watch-utility-grid"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 sm:gap-8"
      >
        {UTILITIES.map((u) => {
          // Card uniformi. L'unica differenza è la scala interna dell'IMMAGINE
          // di Timer (leggermente più grande) e Cronometro (leggermente più
          // piccola). La cella della griglia resta identica per tutte.
          const imgScaleClass =
            u.slug === "timer"
              ? "scale-[1.10]"
              : u.slug === "cronometro"
                ? "scale-[0.90]"
                : "";
          const inner = (
            <>
              <div
                className={`relative aspect-square rounded-xl overflow-hidden bg-black border border-white/5 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)] flex items-center justify-center ${
                  u.ready
                    ? "transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-[0_25px_60px_-15px_rgba(16,185,129,0.35)] group-hover:border-emerald-400/25"
                    : ""
                }`}
              >
                {u.Icon ? (
                  <u.Icon
                    aria-hidden="true"
                    className={`relative w-16 h-16 sm:w-20 sm:h-20 text-white/85 stroke-[1.1] transition-transform duration-500 group-hover:scale-105 ${imgScaleClass}`}
                  />
                ) : (
                  <img
                    src={u.icon}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    draggable={false}
                    className={`absolute inset-0 w-full h-full object-cover select-none opacity-90 transform-gpu origin-center ${imgScaleClass}`}
                  />
                )}
              </div>
              <div className="mt-4 px-1">
                <div
                  className={`text-sm sm:text-base font-light text-white leading-snug tracking-tight text-center ${
                    u.ready ? "group-hover:text-emerald-200 transition-colors" : ""
                  }`}
                >
                  {u.title}
                </div>
              </div>
            </>
          );
          return u.ready ? (
            <Link
              key={u.slug}
              to={u.to}
              data-testid={`utility-card-${u.slug}`}
              className="group block"
            >
              {inner}
            </Link>
          ) : (
            <div
              key={u.slug}
              data-testid={`utility-card-${u.slug}`}
              className="group block cursor-default"
            >
              {inner}
            </div>
          );
        })}
      </div>
      <OraTimeButton />
      <OrientationControlButton />
    </div>
  );
}
